import { create } from 'zustand';
import tradingService from '../services/tradingService';

const useOrderBookStore = create((set, get) => ({
  // State
  orderBookData: {
    sells_w_avg: 0,
    buys_w_avg: 0,
    sells_volume: 0,
    buys_volume: 0,
    top_sell: 0,
    top_buy: 0,
    rate: 0,
    sells: [],
    buys: [],
    ts: 0,
    last_proceed: 0,
    last_update: 0,
    down_multiplier: 1,
    down_send_time: null,
    pair: '',
    last_price: 0
  },
  isLoading: false,
  error: null,
  selectedPair: 'ETH-USDT',
  precision: 1,
  grouping: '1',

  // Actions
  setSelectedPair: (pair) => {
    set({ selectedPair: pair });
    get().fetchOrderBook(pair);
  },

  setPrecision: (precision) => {
    set({ precision, grouping: precision.toString() });
  },

  fetchOrderBook: async (pair = null) => {
    const targetPair = pair || get().selectedPair;
    set({ isLoading: true, error: null });
    
    try {
      // Convert BTC/USDT format to BTC-USDT for API call
      const apiPair = targetPair.replace('/', '-');
      const data = await tradingService.getOrderBookStack(apiPair);
      
      // Check if API data is empty (price = 0, quantity = 0)
      const isEmptyData = !data || 
        !data.sells || 
        data.sells.length === 0 || 
        data.sells[0].price === 0 || 
        data.sells[0].quantity === 0 ||
        !data.buys || 
        data.buys.length === 0 || 
        data.buys[0].price === 0 || 
        data.buys[0].quantity === 0;
      
     
      
      set({ 
        orderBookData: data || get().orderBookData,
        isLoading: false 
      });
      return data;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to fetch order book',
        isLoading: false 
      });
      return null;
    }
  },

     // Processed data getters
       getProcessedSellOrders: () => {
      const { orderBookData, precision } = get();
      
      // Check if API data is empty or invalid
      if (!orderBookData || !orderBookData.sells || 
        orderBookData.sells.length === 0 || 
        (orderBookData.sells[0] && (orderBookData.sells[0].price === 0 || orderBookData.sells[0].quantity === 0))) {
        // Return empty array when no valid data
        return [];
      }
      
      // Use real API data
      
      // Group orders by price precision
      const grouped = {};
      orderBookData.sells.forEach(order => {
        if (order.price > 0 && order.quantity > 0) {
          const groupedPrice = Math.floor(order.price / precision) * precision;
          if (!grouped[groupedPrice]) {
            grouped[groupedPrice] = {
              price: groupedPrice,
              quantity: 0,
              total: 0,
            };
          }
          grouped[groupedPrice].quantity += parseFloat(order.quantity);
          grouped[groupedPrice].total += parseFloat(order.quantity) * parseFloat(order.price);
        }
      });

      // Calculate depth and sort
      let cumulative = 0;
      return Object.values(grouped)
        .sort((a, b) => b.price - a.price)
        .map(order => {
          cumulative += order.quantity;
          return { ...order, depth: cumulative };
        })
        .slice(0, 15); // Show top 15 sell orders
    },

           getProcessedBuyOrders: () => {
        const { orderBookData, precision } = get();
        
        // Check if API data is empty or invalid
        if (!orderBookData || !orderBookData.buys || 
          orderBookData.buys.length === 0 || 
          (orderBookData.buys[0] && (orderBookData.buys[0].price === 0 || orderBookData.buys[0].quantity === 0))) {
          // Return empty array when no valid data
          return [];
        }
        
        // Use real API data
        
        // Group orders by price precision
        const grouped = {};
        orderBookData.buys.forEach(order => {
          if (order.price > 0 && order.quantity > 0) {
            const groupedPrice = Math.floor(order.price / precision) * precision;
            if (!grouped[groupedPrice]) {
              grouped[groupedPrice] = {
                price: groupedPrice,
                quantity: 0,
                total: 0,
              };
            }
            grouped[groupedPrice].quantity += parseFloat(order.quantity);
            grouped[groupedPrice].total += parseFloat(order.quantity) * parseFloat(order.price);
          }
        });

        // Calculate depth and sort
        let cumulative = 0;
        return Object.values(grouped)
          .sort((a, b) => b.price - a.price)
          .map(order => {
            cumulative += order.quantity;
            return { ...order, depth: cumulative };
          })
          .slice(0, 15); // Show top 15 buy orders
      },

  getMaxDepth: () => {
    const sellOrders = get().getProcessedSellOrders();
    const buyOrders = get().getProcessedBuyOrders();
    
    const maxSellDepth = sellOrders.length > 0 ? sellOrders[sellOrders.length - 1]?.depth || 0 : 0;
    const maxBuyDepth = buyOrders.length > 0 ? buyOrders[buyOrders.length - 1]?.depth || 0 : 0;
    
    return Math.max(maxSellDepth, maxBuyDepth);
  },

  getSpread: () => {
    const sellOrders = get().getProcessedSellOrders();
    const buyOrders = get().getProcessedBuyOrders();
    
    if (sellOrders.length > 0 && buyOrders.length > 0) {
      const lowestSell = sellOrders[sellOrders.length - 1]?.price || 0;
      const highestBuy = buyOrders[0]?.price || 0;
      return lowestSell - highestBuy;
    }
    return 0;
  },

        getCurrentPrice: () => {
        const { orderBookData } = get();
        
        // Check if orderBookData exists and has valid price data
        if (!orderBookData || (!orderBookData.last_price && !orderBookData.rate)) {
          return 0;
        }
        
        // Use real API current price
        return orderBookData.last_price || orderBookData.rate || 0;
      },

  clearError: () => {
    set({ error: null });
  },

  // Auto-refresh functionality
  startAutoRefresh: (interval = 2000) => {
    const refreshInterval = setInterval(() => {
      get().fetchOrderBook();
    }, interval);
    
    return () => clearInterval(refreshInterval);
  }
}));

export default useOrderBookStore;